import type { FC } from "react";
import React, { memo, useCallback } from "react";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../../shared-components/css-modules/toyota/atoms/Button";
import styles from "./Overview.module.scss";
import a11y from "../../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../../redux/store";
import { CarConfigLabel } from "../../../../../../utils/labelUtils";
import Price from "../../../../common/price/Price";
import { removeExtra } from "../../../../../../redux/slices/CarConfigBasketSlice";

import { CarConfigConfigureSection } from "../../../../../../utils/types/SectionTypes";

import { trackSummaryCardRemoveHomeCharger } from "../../../../../../utils/tracking";
import { scrollToTarget } from "../../../../../../../../../common-deprecated/utils/scrollUtils";
import Icon from "../../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import useCarConfigPublicChargers from "../../../../../../hooks/useCarConfigChargers";
import { setActiveSection } from "../../../../../../redux/slices/CarConfigNavigationSlice";
import OverviewItemWrapper from "./OverviewItemWrapper";
import { getChargingVisual } from "../../../../../../utils/visualUtils";
import { hasChargingSelector } from "../../../../../../utils/selectorUtils";

const OverviewChargers: FC = () => {
    const dispatch = useCarConfigDispatch();
    const publicCharger = useCarConfigPublicChargers();
    const homeChargers = useCarConfigSelector(
        (state) => state.basket.extras.filter((extra) => extra.isCharger),
        shallowEqual,
    );
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const showCharging = useCarConfigSelector(hasChargingSelector, shallowEqual);

    const hasHomeChargers = homeChargers.length !== 0;

    const handleClick = useCallback(() => {
        scrollToTarget(CarConfigConfigureSection.Charging);

        const verticalScroll = window.matchMedia("(min-width: 1200px)");
        if (!verticalScroll.matches) {
            dispatch(setActiveSection(CarConfigConfigureSection.Charging));
        }
    }, [dispatch]);

    const imageChargers = getChargingVisual(homeChargers, commonSettings);

    if (!showCharging) return null;
    return (
        <OverviewItemWrapper
            title="carConfigChargingOptions"
            editSection={CarConfigConfigureSection.Charging}
            image={imageChargers}
        >
            <div>
                {hasHomeChargers || publicCharger ? (
                    // Role list needed for accessibility (mainly in safari)
                    // eslint-disable-next-line jsx-a11y/no-redundant-roles
                    <ul role="list" className={styles.sublist}>
                        {publicCharger && (
                            <li className={styles.listitem}>
                                <div>
                                    <Text variant="apheleia-body" tag="h4" spacing="spacing-0">
                                        {publicCharger.publicChargingTitle}
                                    </Text>
                                </div>
                                <div className={styles.listprice}>
                                    <Text variant="apheleia-body" spacing="spacing-0">
                                        <span className={styles.included}>
                                            <CarConfigLabel label="carConfigIncluded" />
                                            <span className={styles["included-icon"]}>
                                                <Icon variant="check" sizing="xs" aria-hidden="true" />
                                            </span>
                                        </span>
                                    </Text>
                                </div>
                            </li>
                        )}
                        {homeChargers.map((homeCharger) => (
                            <li key={homeCharger.id} className={styles.listitem}>
                                <div>
                                    <Text variant="apheleia-body" tag="h4" spacing="spacing-0">
                                        {homeCharger.name}
                                    </Text>
                                </div>
                                <div className={styles.listprice}>
                                    <Text variant="apheleia-body" spacing="spacing-0">
                                        {homeCharger.price.cash === 0 ? (
                                            <span className={styles.included}>
                                                <CarConfigLabel label="carConfigIncluded" />
                                                <span className={styles["included-icon"]}>
                                                    <Icon variant="check" sizing="xs" aria-hidden="true" />
                                                </span>
                                            </span>
                                        ) : (
                                            <span className={styles["listprice-price"]}>
                                                +&nbsp;
                                                <Price price={homeCharger.price} forceCashPrice />
                                            </span>
                                        )}
                                    </Text>
                                </div>
                                <div>
                                    <Button
                                        variant="btn-default"
                                        circle
                                        sizing="xs"
                                        onClick={() => {
                                            trackSummaryCardRemoveHomeCharger();
                                            dispatch(removeExtra({ extra: homeCharger }));
                                        }}
                                    >
                                        <Icon variant="minus" sizing="xs" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigOverviewChargingRemove" />
                                        </span>
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <Button variant="apheleia-text" onClick={handleClick}>
                        <CarConfigLabel label="carConfigOverviewChargingBrowse" />
                    </Button>
                )}
            </div>
        </OverviewItemWrapper>
    );
};

export default memo(OverviewChargers);
